import { abilityEffectId, abilityEffectTicks, CONTROL_LIMITS, DIMINISHING_PERCENTS, EffectLimit, SAFE_PASSAGE, blowOf, diminishedTicks, type Ability, type Chain, type ControlKind, type Effect, type EffectModifiers, type ResourceId, type Rules } from "odyssey-shared";
import { penaltyFollows, penaltyStacks } from "../death/penalty-words.ts";
import { resourceName } from "../fight/wording.ts";
import { durationText, listed, signedPercent, stepText, tilesText } from "./units.ts";
import { effectTone } from "./layout.ts";
import { EN } from "odyssey-lang";
import { say } from "../../words.ts";

/**
 * The lines an effect's `tip` may name, the keys of the language files' `bar.effectTips`.
 */
type EffectTipLine = Extract<keyof typeof EN.words.bar.effectTips, string>;

/**
 * The ability bar's and the effects rows' tooltips, drafted from the rules tables (P2.2-T12, ADR-079's amendment of
 * 2026-10-02 "tooltips"): every line is built from an ability's or an effect's record and the constants, never written
 * per record, so a record added to the tables gets its tooltip with no wording of its own. Every word is a draft for
 * the author. Pure: the page builds a tooltip only when one opens.
 */

/**
 * How a tooltip line is coloured (the design's `RichTooltip` stats): plain text, a benefit, a harm, a caution, or a muted note.
 */
export type TipTone = "plain" | "good" | "bad" | "warning" | "muted";

/**
 * One line of a tooltip.
 */
export interface TipLine {
  readonly text: string;
  readonly tone: TipTone;
}

/**
 * An ability slot's tooltip: the ability's name and its slot's key, where it comes from, each cost in its resource's
 * colour, its reach, its cooldown, and what it does, line by line.
 */
export interface AbilityTip {
  readonly kind: "ability";
  readonly name: string;
  readonly key: string;
  readonly typeLine: string;
  readonly costs: readonly { readonly text: string; readonly resource: ResourceId }[];
  readonly range: string;
  readonly cooldown: string;
  readonly lines: readonly TipLine[];
}

/**
 * An empty slot's tooltip: the words and the slot's key.
 */
export interface EmptyTip {
  readonly kind: "empty";
  readonly name: string;
  readonly key: string;
}

/**
 * A label's tooltip, its words alone: a wallet mark's (P2.8-T67, ADR-124's note for P2.8-T67).
 */
export interface LabelTip {
  readonly kind: "label";
  readonly name: string;
}

/**
 * An effect's tooltip: its name, whether it is a buff or a debuff (the view adds the time left beside it), and what it
 * does, line by line.
 */
export interface EffectTip {
  readonly kind: "effect";
  readonly name: string;
  readonly typeLine: string;
  readonly lines: readonly TipLine[];
}

/**
 * The XP bar's tooltip (P2.4-T8, `hud/xp-rate.ts`): the level, the XP into it or the cap, and the rate's lines.
 */
export interface XpTip {
  readonly kind: "xp";
  readonly name: string;
  readonly typeLine: string;
  readonly lines: readonly TipLine[];
}

/**
 * The stance button's tooltip (P2.2-T13, `hud/stance-button.tsx`): the stance, its key, and what a click does in it.
 */
export interface StanceTip {
  readonly kind: "stance";
  readonly name: string;
  readonly key: string;
  readonly typeLine: string;
  readonly lines: readonly TipLine[];
}

/**
 * Any tooltip the bar, the rows, the XP bar, the stance button and the wallet line show.
 */
export type HudTip = AbilityTip | EmptyTip | LabelTip | EffectTip | XpTip | StanceTip;

/**
 * How many blows a chain strikes, in words: One to Eight, the number itself past that, and nothing for none.
 *
 * @param hits The number of blows.
 * @returns The word.
 */
function blowCount(hits: number): string {
  const counts = ["bar.blowCounts.n1", "bar.blowCounts.n2", "bar.blowCounts.n3", "bar.blowCounts.n4", "bar.blowCounts.n5", "bar.blowCounts.n6", "bar.blowCounts.n7", "bar.blowCounts.n8"] as const;
  if (hits === 0) {
    return "";
  }
  const key = counts[hits - 1];
  return key === undefined ? String(hits) : say(key);
}

/**
 * A chain's damage line (ADR-073's amendment "the Strike combo and the Heavy Strike chain", item 10, a draft for the
 * author): its blows' amounts, never their sum, since each lands only on what its cone reaches at its own tick.
 *
 * @param chain The chain.
 * @param type The ability's damage type.
 * @returns The sentence.
 * @example
 * chainDamageLine(heavyStrike.chain, "physical");
 * // "Three blows of 4, 5 and 6 physical (base). Each lands only on what is in front of you at the time."
 */
function chainDamageLine(chain: Chain, type: string): string {
  const amounts = chain.hits.map((hit) => String(hit.amount));
  const list = say("bar.chainAmounts", { first: amounts.slice(0, -1).join(", "), last: amounts[amounts.length - 1] as string });
  return say("bar.chainDamage", { count: blowCount(chain.hits.length), amounts: list, type });
}

/**
 * An effect line's opening for an ability: a chain's effects land from its last blow alone, so its lines say so,
 * each opening a sentence of the language files written whole.
 *
 * @param chain The ability's chain, or null.
 * @param control The control the effect is, or null for one that is applied.
 * @returns The opening.
 */
function effectOpening(chain: Chain | null, control: ControlKind | null): string {
  if (control === null) {
    return chain === null ? say("bar.applies") : say("bar.lastBlowApplies");
  }
  return chain === null ? say(`bar.controlVerbs.${control}`) : say(`bar.lastBlowVerbs.${control}`);
}

/**
 * What each limit forbids, as the effect's tooltip lists it, in bit order.
 */
const LIMIT_VERBS: readonly (readonly [number, "move" | "act"])[] = [
  [EffectLimit.Move, "move"],
  [EffectLimit.Act, "act"],
];

/**
 * The duration a running controlling effect was applied for, which the diminishing steps scale. The wire carries no
 * source, so it is read from the longest time left seen (`span`), which a first sighting mid-way (a target selected or
 * a page loaded during the effect) can leave below the real start. The candidates are the effect's own duration and
 * every duration an ability names for it (ADR-079's amendment of 2026-10-21); the winner is the one with a step of the
 * diminishing returns that is at least `span` and nearest to it, the longest on a tie, and the effect's own duration
 * when none is seen or none reaches `span`. The reading is a best guess: a shorter own duration seen at a diminished step
 * of a longer named one can be told apart from the named duration only by the time left, and may read as either.
 *
 * @param rules The rules.
 * @param effect The effect.
 * @param span The longest time left in ticks seen, 0 for none.
 * @returns The duration in ticks.
 */
function runningDuration(rules: Rules, effect: Effect, span: number): number {
  const candidates = [effect.durationTicks];
  for (const ability of rules.tables.abilities) {
    for (const entry of ability.effects) {
      const named = abilityEffectTicks(entry);
      if (named !== null && abilityEffectId(entry) === effect.id) {
        candidates.push(named);
      }
    }
  }
  let found = effect.durationTicks;
  let gap = Number.POSITIVE_INFINITY;
  if (span <= 0) {
    return found;
  }
  for (const candidate of candidates) {
    for (let landed = 0; landed < DIMINISHING_PERCENTS.length; landed += 1) {
      const left = diminishedTicks(candidate, landed) - span;
      if (left >= 0 && (left < gap || (left === gap && candidate > found))) {
        found = candidate;
        gap = left;
      }
    }
  }
  return found;
}

/**
 * The diminishing returns' line for a controlling effect (Bible 5.5, ADR-079 decision 5; the author's wording of
 * 2026-10-03): how long each application within the window lasts, from the effect's duration through
 * `diminishedTicks`, and that every later one lasts the last step (the author's ruling of 2026-10-07: no immunity).
 *
 * @param rules The rules.
 * @param effect The effect, whose control is not null.
 * @param span The longest time left in ticks the bearer's row has seen of it, 0 for none seen.
 * @returns The line.
 * @example
 * diminishingLine(rules, rules.effects.get("stun"), 0).text;
 * // "Stunned again within 6 s: 1 s, then 0.5 s, then 0.25 s. Every later one lasts a quarter."
 */
function diminishingLine(rules: Rules, effect: Effect, span: number): TipLine {
  const duration = runningDuration(rules, effect, span);
  const steps = DIMINISHING_PERCENTS.map((_, landed) => stepText(diminishedTicks(duration, landed))).reduce((before, after) => say("bar.thenStep", { before, after }));
  return {
    text: say("bar.diminishing", { effect: effect.name, window: durationText(rules.constants.diminishing_window), steps }),
    tone: "muted",
  };
}

/**
 * Where an ability comes from: its weapon's blow, or every character for one that is given.
 *
 * @param rules The rules.
 * @param ability The ability.
 * @returns The type line.
 */
function abilityTypeLine(rules: Rules, ability: Ability): string {
  for (const weapon of rules.tables.weapons) {
    const blow = weapon.blows.find((entry) => entry.ability === ability.id);
    if (blow !== undefined) {
      return say("bar.weaponBlow", { weapon: weapon.name, blow: blow.blow === "basic" || blow.blow === "heavy" ? say(`bar.blows.${blow.blow}`) : blow.blow });
    }
  }
  return ability.given ? say("bar.everyCharacter") : say("bar.ability");
}

/**
 * What an ability's shape reaches, as the tooltip's first line: its targets and where, oneself or an ally for an ability
 * aimed at a friend (ADR-079's amendment of 2026-10-03), oneself for a record aimed at its caster alone (ADR-091
 * decision 2), a shot's landing and its burst (ADR-095 decision 2), or, for a displacement, the dash.
 *
 * @param ability The ability.
 * @returns The line.
 */
function shapeLine(ability: Ability): string {
  const shape = ability.shape;
  if (shape.kind === "target") {
    return say("bar.shape.target", { range: tilesText(shape.rangeSixteenths) });
  }
  if (shape.kind === "ally") {
    return say("bar.shape.ally", { range: tilesText(shape.rangeSixteenths) });
  }
  if (shape.kind === "self") {
    return say("bar.shape.self");
  }
  if (shape.kind === "line" && ability.displacement !== null) {
    return say("bar.shape.dash", { length: tilesText(shape.lengthSixteenths), time: durationText(ability.displacement.ticks) });
  }
  const who = shape.maxTargets === 1 ? say("bar.nearest") : say("bar.upTo", { count: String(shape.maxTargets) });
  if (shape.kind === "projectile") {
    return say("bar.shape.projectile", { range: tilesText(shape.rangeSixteenths), who, radius: tilesText(shape.radiusSixteenths) });
  }
  return shape.kind === "cone" ? say("bar.shape.cone", { who, degrees: String(shape.degrees) }) : say("bar.shape.line", { who, length: tilesText(shape.lengthSixteenths) });
}

/**
 * An ability's reach: a cone's radius, a line's length, a target's, an ally's or a shot's range, or `Self` for a record aimed at
 * its caster alone.
 *
 * @param ability The ability.
 * @returns The text.
 */
function rangeText(ability: Ability): string {
  const shape = ability.shape;
  if (shape.kind === "self") {
    return say("bar.rangeSelf");
  }
  return tilesText(shape.kind === "cone" ? shape.radiusSixteenths : shape.kind === "line" ? shape.lengthSixteenths : shape.rangeSixteenths);
}

/**
 * An ability slot's tooltip, from the ability's record. The starting weapon's basic blow, when it has a cost, says last
 * what it does when that cost cannot be paid (ADR-117 decision 9; the author approved the sentence): it names no fraction, since
 * the share is a constant rounded up.
 *
 * @param rules The rules, which name its weapon and its effects.
 * @param ability The ability.
 * @param key The slot's key.
 * @returns The tooltip.
 * @example
 * abilityTip(rules, rules.abilities.get("heavy_strike"), "2").cooldown;
 * // "8 s"
 */
export function abilityTip(rules: Rules, ability: Ability, key: string): AbilityTip {
  const lines: TipLine[] = [{ text: shapeLine(ability), tone: "plain" }];
  const chain = ability.chain;
  if (ability.damage !== null && chain !== null) {
    lines.push({ text: chainDamageLine(chain, ability.damage.type), tone: "plain" });
    lines.push({ text: say("bar.rooted", { time: durationText(chain.rootTicks) }), tone: "plain" });
  } else if (ability.damage !== null) {
    lines.push({ text: say("bar.deals", { amount: String(ability.damage.amount), type: ability.damage.type }), tone: "plain" });
  }
  for (const entry of ability.effects) {
    const effect = rules.effects.get(abilityEffectId(entry));
    if (effect === undefined) {
      continue;
    }
    if (effect.control === null) {
      lines.push({ text: say("bar.effectFor", { opening: effectOpening(chain, null), effect: effect.name, time: durationText(abilityEffectTicks(entry) ?? effect.durationTicks), unless: chain === null ? "" : say("bar.unlessBlocked") }), tone: "plain" });
    } else {
      lines.push({ text: say("bar.controlFor", { opening: effectOpening(chain, effect.control), time: durationText(abilityEffectTicks(entry) ?? effect.durationTicks), unless: chain === null ? "" : say("bar.unlessBlocked") }), tone: "plain" });
    }
  }
  for (const chance of ability.onHit) {
    const effect = rules.effects.get(chance.effect);
    if (effect !== undefined) {
      const shortest = Math.min(chance.light.minTicks, chance.normal.minTicks, chance.critical.minTicks);
      const longest = Math.max(chance.light.maxTicks, chance.normal.maxTicks, chance.critical.maxTicks);
      const time = shortest === longest ? durationText(shortest) : say("bar.timeRange", { shortest: durationText(shortest), longest: durationText(longest) });
      lines.push({ text: say("bar.chance", { percent: String(chance.chanceBasisPoints / 100), effect: effect.name, time }), tone: "plain" });
    }
  }
  for (const id of ability.cleanses) {
    const effect = rules.effects.get(id);
    if (effect !== undefined) {
      lines.push({ text: say("bar.removes", { effect: effect.name }), tone: "plain" });
    }
  }
  const unpaid = ability.costs[0];
  if (unpaid !== undefined && ability === blowOf(rules, rules.startingWeapon, "basic")) {
    lines.push({ text: say("bar.unpaid", { resource: resourceName(unpaid.resource) }), tone: "plain" });
  }
  return {
    kind: "ability",
    name: ability.name,
    key,
    typeLine: abilityTypeLine(rules, ability),
    costs: ability.costs.map((cost) => ({ text: say("bar.cost", { amount: String(cost.amount), resource: resourceName(cost.resource) }), resource: cost.resource })),
    range: rangeText(ability),
    cooldown: durationText(ability.cooldownTicks),
    lines,
  };
}

/**
 * An empty slot's tooltip.
 *
 * @param key The slot's key.
 * @returns The tooltip.
 */
export function emptyTip(key: string): EmptyTip {
  return { kind: "empty", name: say("bar.empty"), key };
}

/**
 * The modifiers' lines, each counted once per stack, in the record's field order; a field of 0 or left out says nothing.
 *
 * @param modifiers The effect's modifiers, per stack.
 * @param stacks The stacks it holds.
 * @returns The lines.
 */
function modifierLines(modifiers: EffectModifiers, stacks: number): TipLine[] {
  const lines: TipLine[] = [];
  const dealt = modifiers.damageDealt * stacks;
  const taken = modifiers.damageTaken * stacks;
  const health = modifiers.maxHealth * stacks;
  const cooldown = modifiers.cooldown * stacks;
  const absorb = modifiers.absorb * stacks;
  const focus = (modifiers.focusRegeneration ?? 0) * stacks;
  const speed = (modifiers.moveSpeed ?? 0) * stacks;
  if (dealt !== 0) {
    lines.push({ text: say("bar.modifiers.damageDealt", { percent: signedPercent(dealt) }), tone: dealt > 0 ? "good" : "bad" });
  }
  if (taken !== 0) {
    lines.push({ text: say("bar.modifiers.damageTaken", { percent: signedPercent(taken) }), tone: taken < 0 ? "good" : "bad" });
  }
  if (health !== 0) {
    lines.push({ text: say("bar.modifiers.maxHealth", { percent: signedPercent(health) }), tone: health > 0 ? "good" : "bad" });
  }
  if (cooldown !== 0) {
    lines.push({ text: say("bar.modifiers.cooldown", { percent: signedPercent(cooldown) }), tone: cooldown > 0 ? "bad" : "good" });
  }
  if (absorb !== 0) {
    lines.push({ text: say("bar.modifiers.absorb", { amount: String(absorb) }), tone: "good" });
  }
  if (focus !== 0) {
    lines.push({ text: say("bar.modifiers.focusRegeneration", { percent: signedPercent(focus) }), tone: focus > 0 ? "good" : "bad" });
  }
  if (speed !== 0) {
    lines.push({ text: say("bar.modifiers.moveSpeed", { percent: signedPercent(speed) }), tone: speed > 0 ? "good" : "bad" });
  }
  return lines;
}

/**
 * What a control effect forbids, from its category's limits.
 *
 * @param control The category.
 * @returns The line.
 */
function forbidsLine(control: ControlKind): TipLine {
  const limits = CONTROL_LIMITS[control];
  const verbs = LIMIT_VERBS.filter(([bit]) => (limits & bit) !== 0).map(([, verb]) => say(`bar.limitVerbs.${verb}`));
  return { text: say("bar.cannot", { verbs: verbs.length === 0 ? "" : verbs.reduce((first, second) => say("bar.either", { first, second })) }), tone: "bad" };
}

/**
 * An effect's tooltip, from the effect's record. The wire carries no effect's source, so the tooltip names the
 * abilities whose records apply it, each name once, and the death a penalty follows. The safe passage, whose shelter is
 * the flag rule's and no field of its record, says what it shelters from (ADR-076's amendment of 2026-10-03).
 *
 * @param rules The rules, which name the abilities that apply it and the diminishing returns' constants.
 * @param effect The effect.
 * @param stacks The stacks it holds now, from the frame.
 * @param span The longest time left in ticks the bearer's row has seen of it, which tells the duration it runs for.
 * @returns The tooltip.
 * @example
 * effectTip(rules, rules.effects.get("stun"), 1).lines[0];
 * // { text: "Cannot move or act.", tone: "bad" }
 */
export function effectTip(rules: Rules, effect: Effect, stacks: number, span = 0): EffectTip {
  const lines: TipLine[] = [];
  if (effect.id === SAFE_PASSAGE) {
    lines.push({ text: say("bar.passage"), tone: "good" });
  }
  if (effect.control !== null) {
    lines.push(forbidsLine(effect.control));
  }
  if (effect.periodic !== null) {
    lines.push({ text: say("bar.periodic", { amount: String(effect.periodic.amount * Math.max(1, stacks)), type: effect.periodic.type, time: durationText(effect.periodic.everyTicks) }), tone: "bad" });
  }
  lines.push(...modifierLines(effect.modifiers, Math.max(1, stacks)));
  if (effect.control !== null) {
    lines.push(diminishingLine(rules, effect, span));
  }
  if (effect.tip !== undefined && Object.hasOwn(EN.words.bar.effectTips, effect.tip)) {
    lines.push({ text: say(`bar.effectTips.${effect.tip as EffectTipLine}`), tone: "muted" });
  }
  if (effect.maxStacks > 1) {
    lines.push({ text: penaltyStacks(stacks, effect.maxStacks), tone: "muted" });
  }
  if (effect.onDeath !== null) {
    lines.push({ text: penaltyFollows(effect.onDeath), tone: "muted" });
  }
  const sources = [...new Set(rules.tables.abilities.filter((ability) => ability.effects.some((entry) => abilityEffectId(entry) === effect.id) || ability.onHit.some((chance) => chance.effect === effect.id)).map((ability) => ability.name))];
  if (sources.length > 0) {
    lines.push({ text: say("bar.appliedBy", { abilities: listed(sources) }), tone: "muted" });
  }
  return { kind: "effect", name: effect.name, typeLine: effectTone(effect) === "debuff" ? say("bar.debuff") : say("bar.buff"), lines };
}

/**
 * Which block of the design's `RichTooltip` a line sits in: the stats (a benefit, a harm or a caution), the description (plain)
 * or the notes (muted).
 *
 * @param line The line.
 * @returns The block.
 */
function blockOf(line: TipLine): "stats" | "plain" | "muted" {
  return line.tone === "good" || line.tone === "bad" || line.tone === "warning" ? "stats" : line.tone;
}

/**
 * A tooltip's lines in the design's blocks: each run of consecutive lines of one block together, the view putting the
 * design's 3 between blocks and nothing between the lines of one.
 *
 * @param lines The lines.
 * @returns The runs, in order.
 * @example
 * tipGroups([{ text: "Damage dealt -5%", tone: "bad" }, { text: "Maximum health -2%", tone: "bad" }]).length;
 * // 1
 */
export function tipGroups(lines: readonly TipLine[]): readonly (readonly TipLine[])[] {
  const groups: TipLine[][] = [];
  for (const line of lines) {
    const last = groups[groups.length - 1];
    const previous = last?.[last.length - 1];
    if (last !== undefined && previous !== undefined && blockOf(previous) === blockOf(line)) {
      last.push(line);
    } else {
      groups.push([line]);
    }
  }
  return groups;
}
